/**
 * 存放天数。
 *
 * 设计取舍（原来的临期机制已经整个去掉，别再引回来）：
 *   之前是"系统按字典猜保质期，算出到期日，再判断临期"。
 *   问题是猜不准，而且对短保质期食材直接失灵 —— 菠菜按 4 天算，
 *   入库第二天就报临期，提醒从进门起就一直亮着，等于没有提醒。
 *
 *   现在只做一件事：**记下这东西放了几天，如实告诉用户**。
 *   该不该吃由人判断，不由这个函数判断。
 *
 * 实现上必须注意：天数不能存成一个静态数字，否则它永远停在录入那天。
 * 所以库里存的是"开始存放的日期"这个锚点，天数每次按今天实时算。
 * 界面上用户只填和只看"已放几天"，不接触日期。
 */

import type { FridgeItem } from './contracts';

/** 解析 YYYY-MM-DD 为**本地时区**的当天零点。 */
export function parseDateOnly(value: string): Date | null {
	const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
	if (!match) {
		const fallback = new Date(value);
		if (Number.isNaN(fallback.getTime())) return null;
		const copy = new Date(fallback);
		copy.setHours(0, 0, 0, 0);
		return copy;
	}
	const [, year, month, day] = match;
	// 不能用 new Date('2026-09-18')：那会被当成 UTC 午夜，
	// 在东八区直接变成前一天早上 8 点，所有天数集体差一天。
	return new Date(Number(year), Number(month) - 1, Number(day));
}

function startOfDay(date: Date): Date {
	const copy = new Date(date);
	copy.setHours(0, 0, 0, 0);
	return copy;
}

export function toDateOnly(date: Date): string {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
}

export function todayISO(now: Date = new Date()): string {
	return toDateOnly(now);
}

/**
 * 已放天数。当天放进来的算 0 天。
 * storedAt 为空时返回 0（数据没填就当今天）。
 */
export function daysStored(storedAt: string | null, now: Date = new Date()): number {
	if (!storedAt) return 0;
	const target = parseDateOnly(storedAt);
	if (!target) return 0;
	const diff = startOfDay(now).getTime() - target.getTime();
	// 用户把日期填到了未来时，按 0 处理，不返回负数
	return Math.max(0, Math.round(diff / 86_400_000));
}

/**
 * 反算锚点日期：用户说"已经放了 3 天"，那么开始存放日就是 3 天前。
 * 这是录入界面唯一需要用到日期的地方，用户看不到它。
 */
export function storedAtFromDays(days: number, now: Date = new Date()): string {
	const base = startOfDay(now);
	base.setDate(base.getDate() - Math.max(0, Math.floor(days)));
	return toDateOnly(base);
}

/**
 * 人类可读的存放天数。
 *
 * 刻意不做"1 个月""1 年"这类换算：列表和页头副标题要用同一个说法，
 * 一个显示"已放 1 个月"、另一个显示"已经 30 天"，用户会以为是两个数。
 * 天数就老老实实是天数，200 天也照样读得懂。
 */
export function storedDaysText(storedAt: string | null, now: Date = new Date()): string {
  const days = daysStored(storedAt, now);
  if (days === 0) return '今天放的';
  if (days === 1) return '已放 1 天';
  return `已放 ${days} 天`;
}

/** 放得最久的排最前。冰箱列表和"优先用掉"都用它。 */
export function sortByLongestStored(items: FridgeItem[]): FridgeItem[] {
	return [...items].sort((a, b) => daysStored(b.storedAt) - daysStored(a.storedAt));
}

/** 放得最久的几样，供点菜面板和菜谱 prompt 用 */
export function longestStored(items: FridgeItem[], count = 3): FridgeItem[] {
	return sortByLongestStored(items).filter((item) => daysStored(item.storedAt) > 0).slice(0, count);
}
